import { Directive, effect, ElementRef, inject, input } from '@angular/core'
import { LogService } from '../services/log.service'
import { GlobalDataService } from '../services/global-data.service'
import { get } from 'lodash-es'
import { EventService } from '../services/event.service'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { debounceTime } from 'rxjs'


@Directive({
  selector: '[colorScheme]',
})
export class ColorSchemeDirective {
  private _el = inject(ElementRef)
  private _logger = inject(LogService)
  private _globalDataService = inject(GlobalDataService)
  private _eventService = inject(EventService)

  private _currentColorSchemeClass: string | null = null

  colorScheme = input<string>('')
  currentSchemeId = ''

  constructor() {
    effect(() => {
      const colorSchemePath = this.colorScheme()
      this.updateColorScheme(this.getSchemeIdFromPath(colorSchemePath))
    })

    this._eventService.schemeSelected$.pipe(
      takeUntilDestroyed(),
      debounceTime(200),
    ).subscribe(({ schemePath, schemeId }) => {
      if (this.colorScheme() !== schemePath || this.currentSchemeId === schemeId) {
        // don't need to process the updated if the path is different or the scheme ID is the same
        return
      }
      const colorSchemePath = this.colorScheme()
      this.updateColorScheme(this.getSchemeIdFromPath(colorSchemePath))
    })
  }

  private getSchemeIdFromPath(colorSchemePath: string): string {
    if (!colorSchemePath || colorSchemePath.trim() === '') {
      // Don't log warning for empty string - it's a valid "no scheme" state
      return ''
    }
    const { settings } = this._globalDataService.config().theme
    if (!settings) {
      this._logger.warn('ColorSchemeDirective: No theme settings found in config')
      return ''
    }
    return get(settings, colorSchemePath, '') as string
  }

  private buildColorSchemeClass(schemeId: string): string {
    if (!schemeId) {
      this._logger.warn('ColorSchemeDirective: No color scheme ID provided')
      return ''
    }
    return `color-scheme-${schemeId}`
  }

  private updateColorScheme(schemeId: string): void {
    // Remove previous color scheme class if it exists
    if (this._currentColorSchemeClass) {
      this._el.nativeElement.classList.remove(this._currentColorSchemeClass)
      this._currentColorSchemeClass = null
    }

    // If no schemeId, just remove previous class and return
    if (!schemeId || schemeId.trim() === '') {
      this.currentSchemeId = ''
      return
    }

    const schemeClass = this.buildColorSchemeClass(schemeId)
    if (!schemeClass) {
      this._logger.warn('ColorSchemeDirective: No valid color scheme class generated from schemeId: ', schemeId)
      return
    }

    this.currentSchemeId = schemeId

    // Add new color scheme class
    this._el.nativeElement.classList.add(schemeClass)
    this._currentColorSchemeClass = schemeClass
  }
}
